import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@tsgecart/ui';

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Shared across category/product/account pages -- every other feature
 * agent building a storefront page imports this rather than rolling its
 * own breadcrumb markup. The last item is always rendered as plain text
 * with `aria-current="page"`, even if it was given a `to` (a page never
 * links to itself).
 */
export function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  if (items.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className={cn('text-sm text-gray-500', className)}>
      <ol className="flex flex-wrap items-center gap-1">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-1">
              {index > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-300" aria-hidden="true" />}
              {item.to && !isLast ? (
                <Link to={item.to} className="hover:text-brand-foreground hover:underline">
                  {item.label}
                </Link>
              ) : (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className={isLast ? 'font-medium text-gray-900' : ''}
                >
                  {item.label}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
